import { Box, Button, Flex, Text } from '@lideralia/alife-uikit';
import { Icon } from '@lideralia/alife-uikit/dist/atoms/Icon';
import { IconArrowCircleLeft, IconPhoneCall } from '@lideralia/alife-uikit/dist/atoms/Icons';
import { PendantJewelry } from '@lideralia/alife-uikit/dist/atoms/imgs';
import { Container } from '@lideralia/alife-uikit/dist/atoms/Layout';
import { Loader } from '@lideralia/alife-uikit/dist/atoms/Loader';
import { Modal } from '@lideralia/alife-uikit/dist/molecules/Modal';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { KEYS_ANALYTICS, setEventAnalytics } from '../../common/components/Analytics';
import { SocketPayment } from '../../common/components/SocketPayment';
import { actualLanguage } from '../../common/functions/lang';
import { useLine, useLineServiceDetail } from '../../common/hooks';
import { showLoading } from '../../common/schemes/loading';
import { showRegister } from '../../common/schemes/login';
import { postServiceCompanyOrder } from '../../common/schemes/service_company_order';
import { selectServiceCompanyOrder } from '../../common/schemes/service_company_order/selectors';
import {
  postServiceProductOrder,
  putServiceProductOrder,
  resetServiceProductOrder,
} from '../../common/schemes/service_product_order';
import { selectServiceProductOrder } from '../../common/schemes/service_product_order/selectors';
import { showAlert } from '../../common/states/alerts';
import ServiceProduct from './ServiceProduct';
import ServiceProductJewelryStore from './ServiceProductJewelryStore';
import ServiceProductJewelryStoreDetail from './ServiceProductJewelryStoreDetail';

const ServicePage = ({ history, ...props }) => {
  const [stripeConf, setStripeConf] = useState(null);
  const { slug, idLine, idName } = useParams();

  // Hook para gestionar la línea automáticamente
  const { line, loading: lineLoading } = useLine(idLine, idName);

  // Hook para gestionar el detalle del servicio automáticamente
  const {
    serviceDetail,
    loading: serviceLoading,
    reload: reloadServiceDetail,
  } = useLineServiceDetail(idLine, slug);

  const { company, isTesting } = line || {};

  useEffect(() => {
    if (!company) return;

    const stripeApiKey = isTesting
      ? process.env.REACT_APP_STRIPE_KEY_TEST
      : process.env.REACT_APP_STRIPE_KEY;
    const serviceStripeAccountId = serviceDetail?.service_company?.stripe_account_id;
    let stripeAccount;
    if (serviceStripeAccountId) {
      stripeAccount = isTesting ? process.env.REACT_APP_STRIPE_ACCOUNT_TEST : serviceStripeAccountId;
    }

    setStripeConf(
      loadStripe(stripeApiKey, stripeAccount ? { stripeAccount } : undefined)
    );
  }, [company, isTesting, serviceDetail]);

  // Mostrar loading mientras se carga la línea o no hay configuración de Stripe
  if (serviceLoading || lineLoading || !line || stripeConf === null) {
    return (
      <Container paddingY={3} paddingBottom={10} maxWidth={['400px']}>
        <Loader customColor={company?.style?.primaryColor} />
      </Container>
    );
  }

  return (
    <Elements stripe={stripeConf}>
      <ServicePage.Content
        history={history}
        line={line}
        serviceDetail={serviceDetail}
        reloadServiceDetail={reloadServiceDetail}
        {...props}
      />
    </Elements>
  );
};

export { ServicePage };

ServicePage.Content = (props) => {
  const { history, line, serviceDetail, reloadServiceDetail } = props;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  // Extraer datos de la línea que viene como prop
  const { isTesting, company } = line;
  const stripeKey = isTesting
    ? process.env.REACT_APP_STRIPE_KEY_TEST
    : process.env.REACT_APP_STRIPE_KEY;
  const serviceStripeAccountId = serviceDetail?.service_company?.stripe_account_id;
  let stripeAccount;
  if (company && serviceStripeAccountId) {
    stripeAccount = isTesting ? process.env.REACT_APP_STRIPE_ACCOUNT_TEST : serviceStripeAccountId;
  }

  const setShowRegisterState = useCallback((value) => dispatch(showRegister(value)));

  const { slug, idLine, idName } = useParams();

  // Estado local para la orden, similar a EcommercePage
  const [orderState, setOrderState] = useState({
    lineId: null,
    serviceSlug: null,
    serviceProductUuid: null,
    invoice_requested: null,
    fact_name: null,
    fact_surname: null,
    fact_email: null,
    fact_cif: null,
    fact_address: null,
    fact_city: null,
    fact_zipcode: null,
    fact_county: null,
    fact_country_iso2: null,
    payment_method_id: null,
    invoice_with_same_delivery_address: null,
    delivery_name: null,
    delivery_surname: null,
    delivery_email: null,
    delivery_address: null,
    delivery_city: null,
    delivery_zipcode: null,
    delivery_province: null,
    delivery_cif: null,
    delivery_country: null,
    delivery_country_iso2: null,
    service_product_variable_pricing_uuid: null,
    service_product_dedicatory_text: null,
  });

  // Estado para el modal de confirmación
  const [showModal, setShowModal] = useState(false);
  const [showFingerprintModal, setShowFingerprintModal] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const lineServiceDetail = serviceDetail;
  const serviceProductOrder = useSelector(selectServiceProductOrder);
  const serviceCompanyOrder = useSelector(selectServiceCompanyOrder);

  // Dispatch actions
  const setShowLoadingState = useCallback((value) => dispatch(showLoading(value)), [dispatch]);
  const setShowAlert = useCallback(
    (colorType, value, message) => dispatch(showAlert(colorType, value, message)),
    [dispatch]
  );
  const setServiceProductOrderState = useCallback(
    (value) => dispatch(postServiceProductOrder(value)),
    [dispatch]
  );
  const setPutServiceProductOrderState = useCallback(
    (value) => dispatch(putServiceProductOrder(value)),
    [dispatch]
  );
  const setServiceCompanyOrderState = useCallback(
    (value) => dispatch(postServiceCompanyOrder(value)),
    [dispatch]
  );

  const stripe = useStripe();

  const [openProduct, setOpenProduct] = useState(null);
  const [paymentIntentForSocket, setPaymentIntentForSocket] = useState(null);
  const [jewelrySelectedProduct, setJewelrySelectedProduct] = useState(null);
  const [jewelryShowPayment, setJewelryShowPayment] = useState(false);

  const handleToggle = (productId) => {
    setOpenProduct(openProduct === productId ? null : productId);
  };

  // Handle service product order submission
  const handleSubmitServiceProduct = (content, serviceProductUuid) => {
    setDisabledButton(true);
    dispatch(resetServiceProductOrder());
    if (!user.done) {
      setShowRegisterState(1);
      return;
    }
    const processedContent = {
      ...content,
      alias: content.invoice_requested === true ? content.alias : null,
      address: content.invoice_requested === true ? content.address : null,
      country: content.invoice_requested === true ? content.country : null,
      country_iso: content.invoice_requested === true ? content.country_iso : null,
      cp: content.invoice_requested === true ? content.cp : null,
      dni: content.invoice_requested === true ? content.dni : null,
      email: content.invoice_requested === true ? content.email : null,
      location: content.invoice_requested === true ? content.location : null,
    };

    setShowLoadingState(true);
    setEventAnalytics(
      KEYS_ANALYTICS.SERVICE_PRODUCT_TRY_PAYMENT,
      user,
      window.location.pathname,
      line
    );

    const orderData = {
      lineId: idLine,
      serviceSlug: slug,
      serviceProductUuid,
      invoice_requested: processedContent.invoice_requested === true ? 1 : 0,
      fact_name: processedContent.alias,
      fact_surname: processedContent.surname,
      fact_address: processedContent.address,
      fact_county: processedContent.country,
      fact_country_iso2: processedContent.country_iso,
      fact_zipcode: processedContent.cp,
      fact_cif: processedContent.dni,
      fact_email: processedContent.email,
      fact_city: processedContent.location,
      payment_method_id: processedContent.payment_method_id || null,
      ...(jewelrySelectedProduct?.is_deliverable && {
        invoice_with_same_delivery_address: processedContent.invoice_with_same_delivery_address
          ? 1
          : 0,
        delivery_name: processedContent.delivery_alias || null,
        delivery_surname: processedContent.delivery_surname || null,
        delivery_email: processedContent.delivery_email || null,
        delivery_address: processedContent.delivery_address || null,
        delivery_city: processedContent.delivery_location || null,
        delivery_zipcode: processedContent.delivery_cp || null,
        delivery_cif: processedContent.delivery_cif || null,
        delivery_province: processedContent.delivery_province || null,
        delivery_country: processedContent.delivery_country || null,
        delivery_country_iso2: processedContent.delivery_country_iso || null,
      }),
      service_product_variable_pricing_uuid:
        processedContent.service_product_variable_pricing_uuid || null,
      service_product_dedicatory_text: processedContent.service_product_dedicatory_text || null,
    };

    // Actualizar estado local primero
    setOrderState({
      ...orderState,
      ...orderData,
    });

    // Después hacer la llamada API
    setServiceProductOrderState(orderData);
  };

  // Handle modal confirmation for non-payment services
  const handleModalShow = (product) => {
    if (!user.done) {
      setShowRegisterState(1);
      return;
    }
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    if (selectedProduct) {
      if (selectedProduct.type === 'serviceCompany') {
        // Si es un service company, llamar a handleSubmitServiceCompany
        handleSubmitServiceCompany();
      } else {
        // Si es un service product, llamar a handleSubmitServiceProduct
        const contentWithoutPayment = {
          invoice_requested: false,
          // Sin payment_method_id
        };

        handleSubmitServiceProduct(contentWithoutPayment, selectedProduct.uuid);
      }
    }
    setShowModal(false);
    setSelectedProduct(null);
  };

  // Handle service company call to action
  const handleSubmitServiceCompany = () => {
    setShowLoadingState(true);
    setDisabledButton(true);
    setEventAnalytics(
      KEYS_ANALYTICS.SERVICE_COMPANY_TRY_PAYMENT,
      user,
      window.location.pathname,
      line
    );

    const orderData = {
      lineId: idLine,
      serviceSlug: slug,
    };

    // Actualizar estado local primero
    setOrderState({
      ...orderState,
      ...orderData,
    });

    // Después hacer la llamada API
    setServiceCompanyOrderState(orderData);
  };

  useEffect(() => {
    dispatch(resetServiceProductOrder());
  }, []);

  useEffect(async () => {
    if (
      serviceProductOrder &&
      serviceProductOrder.info &&
      serviceProductOrder.done &&
      serviceProductOrder.loading === false
    ) {
      if (serviceProductOrder.info.status === 'requires_action') {
        if (!stripe) {
          return;
        }

        if (serviceProductOrder?.info?.client_secret) {
          setPaymentIntentForSocket(serviceProductOrder?.info?.payment_intent);
        }

        const { error } = await stripe.confirmCardPayment(serviceProductOrder?.info?.client_secret);

        setShowLoadingState(true);

        if (error) {
          setShowLoadingState(false);
          setDisabledButton(false);
          setShowAlert('red', 1, 'Error en la confirmación del pago');
          dispatch(resetServiceProductOrder());
          setTimeout(() => {
            setShowAlert('', -1, '');
          }, 5000);
        }
      } else if (serviceProductOrder.info.status === 'success') {
        setShowLoadingState(false);
        setDisabledButton(false);
        // No llamar a goToServiceProductOrderDetail aquí para evitar doble llamada
        // Se llamará en el useEffect siguiente cuando order_status sea 'order_received'
      }
    } else if (serviceProductOrder.error) {
      setShowLoadingState(false);
      setDisabledButton(false);
      setShowAlert('red', 1, 'Error al procesar la orden del producto');
      dispatch(resetServiceProductOrder());
      setTimeout(() => {
        setShowAlert('', -1, '');
      }, 5000);
    }
  }, [
    serviceProductOrder,
    stripe,
    setShowLoadingState,
    setShowAlert,
    setPutServiceProductOrderState,
  ]);

  // Para manejar cuando el PUT de la orden sea exitoso
  useEffect(() => {
    if (
      serviceProductOrder &&
      serviceProductOrder.info &&
      serviceProductOrder.done &&
      serviceProductOrder.loading === false &&
      serviceProductOrder.info.order_status === 'order_received'
    ) {
      // La orden fue actualizada exitosamente después del pago con Stripe
      goToServiceProductOrderDetail();
    }
  }, [serviceProductOrder]);

  // Para manejar la respuesta del service company order
  useEffect(() => {
    if (
      serviceCompanyOrder &&
      serviceCompanyOrder.info &&
      serviceCompanyOrder.done &&
      serviceCompanyOrder.loading === false
    ) {
      setShowLoadingState(false);
      setDisabledButton(false);
      if (serviceCompanyOrder.info.status === 'success') {
        // redirigir redirigir a la página de detalle de la orden
        goToServiceCompanyOrderDetail();
      } else {
        setShowAlert('red', 1, 'Error al enviar la solicitud de servicio');
        setTimeout(() => {
          setShowAlert('', -1, '');
        }, 5000);
      }
    } else if (serviceCompanyOrder.error) {
      setShowLoadingState(false);
      setDisabledButton(false);
      setShowAlert('red', 1, 'Error al enviar la solicitud de servicio');
      setTimeout(() => {
        setShowAlert('', -1, '');
      }, 5000);
    }
  }, [serviceCompanyOrder, setShowLoadingState, setShowAlert]);

  const goToServiceProductOrderDetail = () => {
    // Forzar recarga del servicio para actualizar el estado de los productos
    // y el flag user_already_ordered después del pago/solicitud
    if (reloadServiceDetail) {
      reloadServiceDetail();
    }

    history.push(`/${actualLanguage}/lines/${idLine}/${idName}/service_product-order-resume`);
  };

  const goToServiceCompanyOrderDetail = () => {
    // Forzar recarga del servicio para actualizar el estado del service_company
    // y el flag user_already_ordered después de la solicitud
    if (reloadServiceDetail) {
      reloadServiceDetail();
    }

    history.push(`/${actualLanguage}/lines/${idLine}/${idName}/service_company-order-resume`);
  };

  const actionGoBack = () => {
    history.goBack();
  };

  // serviceDetail ya viene cargado del componente padre, no necesitamos verificaciones aquí

  if (!lineServiceDetail) {
    return (
      <Container paddingY={3} paddingBottom={10} maxWidth={['400px']}>
        <Text textAlign="center">No line service data available</Text>
      </Container>
    );
  }

  const { service_company, products } = lineServiceDetail;
  const isJewelryStore = slug === 'jewelry-store';
  const ProductComponent = isJewelryStore ? ServiceProductJewelryStore : ServiceProduct;

  return (
    <Container paddingY={3} paddingBottom={10} maxWidth={['400px']}>
      {paymentIntentForSocket && <SocketPayment paymentIntent={paymentIntentForSocket} />}
      <Flex flexDirection="column">
        {/* Service Company Information */}
        {service_company && slug === 'jewelry-store' && (
          <Box
            marginBottom={4}
            backgroundColor="white"
            borderRadius="16px"
            paddingX={3}
            paddingY={2}
            style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e5e5e5' }}
          >
            <Flex alignItems="center">
              <PendantJewelry width={36} height={36} style={{ flexShrink: 0 }} />
              <Text
                color="black96"
                sizeText="display14"
                fontWeight="bold"
                flex="1"
                textAlign="left"
                paddingX={2}
              >
                {service_company.name}
              </Text>
              <Button
                colorType="transparent"
                rounded
                padding={0}
                action={() => {
                  if (jewelrySelectedProduct) {
                    if (jewelryShowPayment) {
                      setJewelryShowPayment(false);
                    } else {
                      setJewelrySelectedProduct(null);
                    }
                  } else {
                    actionGoBack();
                  }
                }}
                borderColor="black36"
                style={{ marginLeft: 'auto', flexShrink: 0 }}
              >
                <Icon sizeIcon="display24" color="black36">
                  <IconArrowCircleLeft />
                </Icon>
              </Button>
            </Flex>
          </Box>
        )}
        {service_company && slug !== 'jewelry-store' && (
          <Box marginBottom={4}>
            <Flex alignItems="start" justifyContent="space-between" marginBottom={2}>
              <Text color="black96" sizeText="display24" fontWeight="bold" textAlign="left">
                {service_company.name}
              </Text>
              <Button
                colorType="transparent"
                rounded
                padding={0}
                action={() => actionGoBack()}
                borderColor="black36"
              >
                <Icon sizeIcon="display24" color="black36">
                  <IconArrowCircleLeft />
                </Icon>
              </Button>
            </Flex>

            {service_company.image_url && (
              <img
                src={service_company.image_url}
                alt={service_company.name}
                width="100%"
                height="auto"
                style={{ borderRadius: '18px', marginBottom: '12px' }}
              />
            )}

            {service_company.description && (
              <Text
                color="black84"
                sizeText="display14"
                textAlign="left"
                paddingBottom={2}
                lineHeight={2}
              >
                {service_company.description}
              </Text>
            )}

            {service_company.call_to_action &&
              service_company.call_to_action_text &&
              !service_company.user_already_ordered && (
                <Button
                  backgroundColor={company?.style?.primaryColor || 'orange'}
                  color={company?.style?.textColor || 'white'}
                  variant="primary"
                  width="100%"
                  onClick={() => handleModalShow({ type: 'serviceCompany' })}
                  paddingBottom={2}
                >
                  {service_company.call_to_action_text}
                </Button>
              )}

            {service_company.call_to_action && service_company.user_already_ordered && (
              <Flex marginTop={2} width="100%" justifyContent="center">
                <Flex
                  flexDirection="row"
                  alignItems="center"
                  backgroundColor="#fdf5e6"
                  border="1px solid orange"
                  borderRadius="100px"
                  width="fit-content"
                  paddingX={2}
                  paddingY="2px"
                >
                  <Text color="black84" sizeText="display12">
                    <FormattedMessage id="serviceProducts.alreadyOrdered" />
                  </Text>
                </Flex>
              </Flex>
            )}
          </Box>
        )}

        {/* Jewelry store product detail view */}
        {isJewelryStore && jewelrySelectedProduct && (
          <ServiceProductJewelryStoreDetail
            product={jewelrySelectedProduct}
            stripeKey={stripeKey}
            stripeAccount={stripeAccount}
            company={company}
            locale={user?.locale}
            userCountry={user?.data?.country_code}
            handleSubmit={(content) =>
              handleSubmitServiceProduct(content, jewelrySelectedProduct.uuid)
            }
            handleBack={() => {
              setJewelrySelectedProduct(null);
              setJewelryShowPayment(false);
            }}
            showPayment={jewelryShowPayment}
            setShowPayment={setJewelryShowPayment}
            buttonDisabled={disabledButton || !!serviceProductOrder?.loading}
          />
        )}

        {/* Products Information */}
        {(!isJewelryStore || !jewelrySelectedProduct) && products && products.length > 0 && (
          <Flex flexDirection="column" width="100%">
            {(() => {
              const includedProducts = products.filter((product) => product.is_included === 1);
              const additionalProducts = products.filter((product) => product.is_included === 0);
              const hasIncluded = includedProducts.length > 0;
              const hasAdditional = additionalProducts.length > 0;

              // If only one type of product, use generic title
              const useGenericTitle = !hasIncluded || !hasAdditional;

              const ProductSection = ({ items = [], included = false }) => {
                if (!items || items.length === 0) return null;

                let titleId;
                if (useGenericTitle) {
                  titleId = 'serviceProducts.services';
                } else if (included) {
                  titleId = 'serviceProducts.servicesIncluded';
                } else {
                  titleId = 'serviceProducts.servicesAdditional';
                }

                return (
                  <Flex
                    flexDirection="column"
                    width="100%"
                    {...(included ? { marginBottom: 2 } : {})}
                    style={isJewelryStore ? { rowGap: '16px' } : undefined}
                  >
                    {!isJewelryStore && (
                      <Text
                        color="black84"
                        sizeText="display24"
                        fontWeight="bold"
                        paddingBottom={2}
                      >
                        <FormattedMessage id={titleId} />
                      </Text>
                    )}

                    {items.map((product, index) => (
                      <ProductComponent
                        key={product.uuid || index}
                        product={product}
                        index={index}
                        stripeKey={stripeKey}
                        stripeAccount={stripeAccount}
                        company={company}
                        locale={user?.locale}
                        handleSubmit={(content) =>
                          handleSubmitServiceProduct(content, product.uuid)
                        }
                        handleModalShow={() => handleModalShow(product)}
                        onToggle={() => handleToggle(product.uuid)}
                        isOpen={openProduct === product.uuid}
                        buttonDisabled={disabledButton || !!serviceProductOrder?.loading}
                        onSelect={
                          isJewelryStore
                            ? (product) => {
                                if (!user.done) {
                                  setShowRegisterState(1);
                                  return;
                                }
                                setJewelrySelectedProduct(product);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }
                            : undefined
                        }
                        onFingerprintBlock={
                          isJewelryStore ? () => setShowFingerprintModal(true) : undefined
                        }
                      />
                    ))}
                  </Flex>
                );
              };

              return (
                <>
                  <ProductSection items={includedProducts} included />
                  <ProductSection items={additionalProducts} />
                </>
              );
            })()}
          </Flex>
        )}
      </Flex>

      {/* Modal para confirmación de servicios sin pago */}
      <Modal
        width="400px"
        isOpen={showModal}
        duration={100}
        onModalClose={() => setShowModal(false)}
        onModalBack={() => setShowModal(false)}
        backWithButton
        marginX={0}
        marginY={0}
        title={<FormattedMessage id="serviceProducts.orderRequestModalTitle" />}
      >
        <Modal.Content>
          <Flex flexDirection="column">
            <div
              style={{
                backgroundColor: company?.style?.primaryColor || 'orange',
                padding: '20px',
                width: '64px',
                height: '64px',
                borderRadius: '50px',
                margin: '0px auto 15px auto',
              }}
            >
              <Icon
                sizeIcon="display24"
                marginRight={2}
                color={company?.style?.textColor || 'white'}
              >
                <IconPhoneCall />
              </Icon>
            </div>

            <Text
              paddingBottom={2}
              textAlign="center"
              sizeText="display16"
              color="black84"
              fontWeight="bold"
            >
              <FormattedMessage id="serviceProducts.orderRequestModalHeader" />
            </Text>

            <Text paddingBottom={4} textAlign="center" sizeText="display14" color="black60">
              <FormattedMessage id="serviceProducts.orderRequestModalBody" />
            </Text>

            <Button
              backgroundColor={company?.style?.primaryColor || 'orange'}
              colorType={company?.style?.primaryColor || 'orange'}
              color={company?.style?.textColor || 'white'}
              fullWidth
              onClick={handleModalConfirm}
              width="100%"
            >
              <FormattedMessage id="serviceProducts.orderRequestModalButton" />
            </Button>
          </Flex>
        </Modal.Content>
      </Modal>

      {/* Fingerprint not available modal */}
      <Modal
        isOpen={showFingerprintModal}
        duration={100}
        onModalClose={() => setShowFingerprintModal(false)}
        onModalBack={() => setShowFingerprintModal(false)}
        backWithButton
        marginX={[3, 3, null]}
        marginY={0}
        title={<FormattedMessage id="serviceProducts.fingerprintModalTitle" />}
      >
        <Modal.Content>
          <Flex flexDirection="column">
            <Text paddingBottom={4} textAlign="center" sizeText="display14" color="black60">
              <FormattedMessage
                id="serviceProducts.fingerprintModalBody"
                values={{ companyName: company?.name || '' }}
              />
            </Text>
          </Flex>
        </Modal.Content>
      </Modal>
    </Container>
  );
};
